import {
  IconFolder,
  IconInbox,
  IconLayoutDashboard,
  IconMessageChatbot,
  IconMessageCircle,
  TablerIcon,
} from "@tabler/icons-react";
import classNames from "classnames";
import { useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { SetBackButtonVisibilityContext } from "@common/context/ThemeContext";
import { DataResponse, useFetchMeData } from "@common/features/User";
import { __ } from "@common/helpers/i18n";
import { CounterBadge } from "@app/elements/text";
import {
  MOBILE_FOOTER_VISIBILITY_EVENT,
  MobileFooterVisibilityEvent,
} from "@app/hooks";

type TranslationKey = Parameters<typeof __>[0];

type FooterTab = {
  route: string;
  icon: TablerIcon;
  label: TranslationKey;
  match?: string[];
  unread?: (data?: DataResponse) => unknown;
};

const tabs = [
  { route: "/dashboard", icon: IconLayoutDashboard, label: "Dashboard" },
  {
    route: "/posts",
    icon: IconFolder,
    label: "Contents",
    match: ["/posts", "/post", "/story", "/draft", "/schedule-calendar"],
  },
  {
    route: "/automation",
    icon: IconMessageChatbot,
    label: "Automation",
  },
  {
    route: "/inbox",
    icon: IconInbox,
    label: "Messages",
    unread: (data) => data?.masterUser?.inbox_unread,
  },
  {
    route: "/comment",
    icon: IconMessageCircle,
    label: "Comments",
    unread: (data) => data?.masterUser?.comment_unread,
  },
] satisfies FooterTab[];

function unreadBadgeValue(value: unknown) {
  const count = Number(value || 0);

  return Number.isFinite(count) && count > 0 ? count : null;
}

export default function Footer() {
  const setBackButtonVisibility = useContext(SetBackButtonVisibilityContext);
  const { data } = useFetchMeData();
  const [isTemporarilyHidden, setIsTemporarilyHidden] = useState(false);

  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    const handleVisibilityChange = (event: Event) => {
      setIsTemporarilyHidden(
        Boolean((event as MobileFooterVisibilityEvent).detail?.hidden),
      );
    };

    window.addEventListener(
      MOBILE_FOOTER_VISIBILITY_EVENT,
      handleVisibilityChange,
    );

    return () => {
      window.removeEventListener(
        MOBILE_FOOTER_VISIBILITY_EVENT,
        handleVisibilityChange,
      );
    };
  }, []);

  useEffect(() => {
    setIsTemporarilyHidden(false);
  }, [location.pathname]);

  const isActive = (menu: FooterTab) => {
    const pathname = location.pathname.replace(/\/$/g, "") || "/";
    const routes = menu.match || [menu.route];

    return routes.some(
      (route) => pathname === route || pathname.startsWith(`${route}/`),
    );
  };

  function redirectTo(location: string) {
    setBackButtonVisibility(false);
    history.push(location);
  }

  function renderItem(item: FooterTab) {
    const Icon = item.icon;
    const active = isActive(item);
    const unread = unreadBadgeValue(item.unread?.(data));

    return (
      <button
        type="button"
        onClick={() => redirectTo(item.route)}
        className={classNames(
          "group flex min-w-0 flex-col items-center justify-center gap-1 rounded-md px-1 py-1 text-muted-foreground transition-colors",
          "hover:text-foreground",
          active && "text-primary",
        )}
        key={item.route}
        aria-current={active ? "page" : undefined}
      >
        <span
          className={classNames(
            "relative flex h-8 w-10 items-center justify-center rounded-md transition-colors",
            active
              ? "bg-primary/10 text-primary ring-1 ring-primary/15"
              : "text-muted-foreground group-hover:bg-muted",
          )}
        >
          <Icon size={19} stroke={active ? 2.25 : 2} />
          {unread && (
            <CounterBadge
              value={unread}
              max={99}
              tone="danger"
              size="xs"
              className="absolute -end-1 -top-1 isolate bg-destructive text-destructive-foreground opacity-100 ring-2 ring-card hover:bg-destructive"
            />
          )}
        </span>
        <span className="w-full truncate text-center text-[10.5px] font-bold leading-4">
          {__(item.label)}
        </span>
      </button>
    );
  }

  return (
    <nav
      aria-label={__("Primary navigation")}
      className={classNames(
        "z-20 flex-shrink-0 border-t border-border bg-card/95 px-2 pt-1.5 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-card/90 md:hidden",
        isTemporarilyHidden ? "hidden" : "flex",
      )}
      style={{
        minHeight: "calc(64px + env(safe-area-inset-bottom))",
        paddingBottom: "max(0.45rem, env(safe-area-inset-bottom))",
      }}
    >
      <div className="grid h-[56px] min-w-0 flex-1 grid-cols-5 gap-1">
        {tabs.map((item) => renderItem(item))}
      </div>
    </nav>
  );
}
